import { useEffect } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore, canAccessRoute } from '@/stores/authStore';
import { ROUTES } from '@/constants/routes';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { PageSpinner } from '@/components/ui/spinner';

export function DashboardLayout() {
  const location = useLocation();
  const { sidebarCollapsed, mobileMenuOpen, setMobileMenuOpen } = useUIStore();
  const { isAuthenticated, isLoading, user } = useAuthStore();

  // Reset scroll to the top on real route changes so each page starts at its
  // header. Keyed on pathname only — expanding/collapsing a sidebar submenu
  // doesn't change the route, so it won't trigger a scroll.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, [location.pathname]);

  // Check authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <PageSpinner message="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // Admins with a temporary password must set a new one before using the panel.
  if (user?.must_change_password) {
    return <Navigate to={ROUTES.CHANGE_PASSWORD} replace />;
  }

  // Check route permissions
  if (user && !canAccessRoute(location.pathname, user.role)) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          'lg:block',
          mobileMenuOpen ? 'block' : 'hidden'
        )}
      >
        <Sidebar />
      </div>

      {/* Header */}
      <Header />

      {/* Main content */}
      <main
        className={cn(
          'min-h-screen pt-16 transition-all duration-300',
          sidebarCollapsed ? 'lg:pl-[68px]' : 'lg:pl-64'
        )}
      >
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
